// frontend/src/utils/logger.ts
/* eslint-disable no-console */
import { toast, ToastOptions, Bounce } from "react-toastify"; // Import react-toastify
import { enqueue } from "./remoteLog";
// We assume uuid is installed or not needed anymore if we don't track specific toast IDs for updates in this simple version.
// If updates are needed, we might need a way to manage toast IDs.

const IS_PROD = import.meta.env.PROD;

// Define LogStatus type (can be kept if used elsewhere, or simplified)
export type LogStatus =
  | "success"
  | "failure"
  | "in-progress"
  | "info"
  | "warning";

// Common Toast Options based on user request
const commonToastOptions: ToastOptions = {
  position: "bottom-right",
  autoClose: 5000, // Default duration
  hideProgressBar: true,
  closeOnClick: false, // Set to false as per user request
  pauseOnHover: true,
  draggable: true,
  theme: "colored",
  transition: Bounce,
};

/**
 * Logger that outputs to console and triggers UI toasts.
 */
const logger = {
  // --- Toast Notification API ---
  /**
   * Logs a message as a toast notification.
   * @param message - The main message for the toast.
   * @param status - The type of toast ('success', 'failure'/'error', 'warning', 'info', 'in-progress' maps to info).
   * @param details - Optional additional details (appended to the message).
   * @param toastId - Optional ID to prevent duplicate toasts or update existing ones.
   * @param duration - Optional custom duration in milliseconds for this specific toast.
   */
  logToast: (
    message: string,
    status: LogStatus = "info",
    details: string = "",
    toastId?: string,
    duration?: number,
  ): void => {
    const fullMessage = details ? `${message} - ${details}` : message;
    // Use provided duration or default, unless status is 'in-progress' which has special handling
    const autoCloseDuration =
      status === "in-progress"
        ? false
        : (duration ?? commonToastOptions.autoClose);
    const options: ToastOptions = {
      ...commonToastOptions,
      toastId,
      autoClose: autoCloseDuration,
    };

    // Map LogStatus to react-toastify types
    switch (status) {
      case "success":
        toast.success(fullMessage, options);
        logger.local.info(`[Toast - Success] ${fullMessage}`);
        break;
      case "failure":
        toast.error(fullMessage, options);
        logger.local.warn(`[Toast - Error] ${fullMessage}`); // Log as warning or error to console
        break;
      case "warning":
        toast.warn(fullMessage, options);
        logger.local.warn(`[Toast - Warning] ${fullMessage}`);
        break;
      case "in-progress":
        // Use info or a custom style if needed for 'in-progress'
        toast.info(fullMessage, { ...options, autoClose: false }); // Keep in-progress toasts open longer or indefinitely
        logger.local.info(`[Toast - In Progress] ${fullMessage}`);
        break;
      case "info":
      default:
        toast.info(fullMessage, options);
        logger.local.info(`[Toast - Info] ${fullMessage}`);
        break;
    }
  },

  /**
   * Updates an existing toast notification by its ID.
   * Note: This requires providing the original toastId when calling logToast.
   * @param toastId - The ID of the toast to update.
   * @param message - The new message.
   * @param status - The new status.
   * @param details - Optional new details.
   * @param duration - Optional custom duration in milliseconds for this specific toast update.
   */
  updateToast: (
    toastId: string,
    message: string,
    status: LogStatus,
    details: string = "",
    _duration?: number,
  ): void => {
    const fullMessage = details ? `${message} - ${details}` : message;
    // Determine autoClose: false for in-progress, specific duration if provided, otherwise default
    const autoCloseDuration =
      status === "in-progress"
        ? false
        : (_duration ?? commonToastOptions.autoClose);

    // Define the update options
    const updateOptions: import("react-toastify").UpdateOptions = {
      // Don't spread options directly here as some might conflict with UpdateOptions specific props
      render: fullMessage, // Set the new content
      // Set the type based on status, mapping in-progress to info
      type:
        status === "failure"
          ? "error"
          : status === "in-progress"
            ? "info"
            : status,
      isLoading: status === "in-progress", // Use isLoading for in-progress state if desired
      autoClose: autoCloseDuration, // Use the calculated duration
      // Inherit other relevant options from commonToastOptions if needed, e.g., theme, position, etc.
      // Be careful not to overwrite essential update properties.
      // Example: theme: commonToastOptions.theme, position: commonToastOptions.position, etc.
      // It might be simpler to let toast.update handle defaults unless specific overrides are needed beyond render, type, isLoading, autoClose.
    };

    // Log the update attempt to console
    switch (status) {
      case "success":
        logger.local.info(`[Toast Update - Success] ${fullMessage}`);
        break;
      case "failure":
        logger.local.warn(`[Toast Update - Error] ${fullMessage}`);
        break;
      case "warning":
        logger.local.warn(`[Toast Update - Warning] ${fullMessage}`);
        break;
      case "in-progress":
        logger.local.info(`[Toast Update - In Progress] ${fullMessage}`);
        break;
      default:
        logger.local.info(`[Toast Update - Info] ${fullMessage}`);
        break;
    }

    // Check if the toast is still active before updating
    if (toast.isActive(toastId)) {
      toast.update(toastId, updateOptions); // Correctly call toast.update
    } else {
      logger.local.warn(
        `[Toast Update] Original toast (ID: ${toastId}) not found. Logging new toast.`,
      );
      logger.logToast(message, status, details, toastId, _duration); // Option: Log as new if original is gone
    }
  },

  // --- Console Logging (remains unchanged) ---
  // Type rest parameters as unknown[]
  debug: (...args: unknown[]) => {
    if (!IS_PROD) {
      // Pass an Error object to potentially improve source map resolution in dev tools
      console.debug("[DEBUG]", ...args, new Error());
    }
  },
  // Type rest parameters as unknown[]
  info: (...args: unknown[]) => {
    // Frontend logs run in the browser and never reach Cloud Logging on their
    // own (there is no stdout to capture — this is a SPA, not Cloud Run). The
    // remote-log transport (ADR-009) ships entries to `POST /api/client-logs`,
    // where the backend scrubs + emits them to stdout so they join the backend
    // Cloud Logging stream. INFO is console-only here and sampled by the
    // transport; ERROR is always shipped (see `error`/`local.error`). PHI must
    // never be logged regardless of sink (CLAUDE.md → Compliance).
    logger.local.info(...args);
  },
  // Type rest parameters as unknown[]
  warn: (...args: unknown[]) => {
    logger.local.warn(...args);
  },
  // Type parameters for error
  error: (
    message: string,
    errorObject?: Error | null,
    ...extraArgs: unknown[]
  ) => {
    // Delegates to `local.error`, which both consoles the structured JSON and
    // ships an ERROR entry to the backend ingest (ADR-009). Never log PHI.
    logger.local.error(message, errorObject, ...extraArgs);
  },
  local: {
    info: (...args: unknown[]) => {
      if (IS_PROD) {
        // Basic structured log for production
        console.info(
          JSON.stringify({
            severity: "INFO",
            source: "frontend",
            timestamp: new Date().toISOString(),
            message: args,
          }),
        );
      } else {
        console.info("[INFO]", ...args, new Error());
      }
    },
    warn: (...args: unknown[]) => {
      if (IS_PROD) {
        console.warn(
          JSON.stringify({
            severity: "WARNING",
            source: "frontend",
            timestamp: new Date().toISOString(),
            message: args,
          }),
        );
      } else {
        console.warn("[WARN]", ...args, new Error());
      }
    },
    error: (
      message: string,
      errorObject?: Error | null,
      ...extraArgs: unknown[]
    ) => {
      // Ship every ERROR to the backend ingest (ADR-009). This is the single
      // sink for `logger.error` (which delegates here), so there is no
      // double-enqueue. The transport samples INFO/WARN but keeps all ERROR.
      enqueue({
        severity: "ERROR",
        message,
        errorType: errorObject?.name ?? null,
        stack: errorObject?.stack ?? null,
      });

      const logData = {
        severity: "ERROR",
        source: "frontend",
        timestamp: new Date().toISOString(),
        message: message,
        error: errorObject
          ? { message: errorObject.message, stack: errorObject.stack }
          : undefined,
        extra: extraArgs.length > 0 ? extraArgs : undefined,
      };

      if (IS_PROD) {
        console.error(JSON.stringify(logData));
      } else {
        // Pass the original error if available, otherwise a new one for stack trace
        console.error(
          "[ERROR]",
          message,
          errorObject || new Error(),
          ...extraArgs,
        );
      }
    },
  },
  /**
   * Logs messages only during development.
   */
  devOnly: {
    // Type rest parameters as unknown[]
    log: (...args: unknown[]) => {
      if (!IS_PROD) {
        console.log("[DEV_ONLY]", ...args, new Error());
      }
    },
    // Type rest parameters as unknown[]
    debug: (...args: unknown[]) => {
      if (!IS_PROD) {
        console.debug("[DEV_ONLY_DEBUG]", ...args, new Error());
      }
    },
    // Type rest parameters as unknown[]
    warn: (...args: unknown[]) => {
      if (!IS_PROD) {
        console.warn("[DEV_ONLY_WARN]", ...args, new Error());
      }
    },
    // Add other levels if needed
  },
  // --- End Console Logging ---
};

export default logger;
